// Cascading panels (docs/design/assessment.md 4.3, 4.10): the columns beside the board.
//
// Columns are [board, panel 1, panel 2, ...]. Opening something from a column docks a panel right
// of it and replaces everything that was further right (one chain at a time, never a tree), except
// pinned panels, which stay. Closing a panel closes those right of it. Reset closes every unpinned
// panel. The chain is in the URL (`?panels=project:180,item:48,notes:48`), so a link opens the same chain
// and Back/Forward walk it. Pure: no DOM, no React; the layout numbers are here too (fold()).

/** a project's board, one item's card with its history, or one item's notes (the whole thread) */
export type PanelKind = "project" | "item" | "notes";

/** What a panel shows: a project's board, one item's card with its history, or one item's notes. */
export interface PanelTarget {
  kind: PanelKind;
  num: number;
}

/** Where a panel came from: the card (or the row on a card) it was opened from, in a column to its left. */
export interface PanelSource {
  /** 0 = the board, 1 = the first panel, ... */
  column: number;
  /** the item whose card or row was clicked */
  num: number;
  /** the card that row is on, when it was a row (a sub-item on its parent's card) */
  via?: number;
}

export interface Panel extends PanelTarget {
  /** stable while it stays open: its filters, scroll and dialogs live with it */
  id: number;
  /** null: opened from a pin, or its source column has gone */
  source: PanelSource | null;
}

export interface Cascade {
  panels: Panel[];
  /** the panel expanded by hand from a spine: it never folds (else the newest never does) */
  expanded: number | null;
  /** the next panel id */
  seq: number;
}

export const EMPTY: Cascade = { panels: [], expanded: null, seq: 1 };

/** The server's pin list decides which panels are pinned; functions here only ask. */
export type IsPinned = (t: PanelTarget) => boolean;
const NONE: IsPinned = () => false;

export const sameTarget = (a: PanelTarget, b: PanelTarget) => a.kind === b.kind && a.num === b.num;

/** The column a panel is in (the board is column 0). */
export const columnOf = (index: number) => index + 1;

const keepExpanded = (panels: Panel[], expanded: number | null) => (expanded != null && panels.some((p) => p.id === expanded) ? expanded : null);

/**
 * Open `target` from column `fromColumn`: it docks right of that column, replacing what was there.
 * Pinned panels further right stay, after it (their source is dropped unless it is in a column that
 * stayed). Opening what is already right there keeps that panel (and its state) and re-ties it.
 */
export function open(c: Cascade, fromColumn: number, target: PanelTarget, source?: Omit<PanelSource, "column"> | null, isPinned: IsPinned = NONE): Cascade {
  const depth = Math.max(0, Math.min(Math.floor(fromColumn), c.panels.length));
  const src: PanelSource | null = source ? { column: depth, num: source.num, ...(source.via != null && source.via !== source.num ? { via: source.via } : {}) } : null;
  const cur = c.panels[depth];
  const same = !!cur && sameTarget(cur, target);
  const panel: Panel = same ? { ...cur, source: src } : { id: c.seq, kind: target.kind, num: target.num, source: src };
  const right = c.panels
    .slice(depth + (same ? 1 : 0))
    .filter((p) => isPinned(p) && !sameTarget(p, target))
    .map((p) => (p.source && p.source.column <= depth ? p : { ...p, source: null }));
  const panels = [...c.panels.slice(0, depth), panel, ...right];
  // the newest panel never folds: a hand-expanded one stops counting once something new opens
  return { panels, expanded: null, seq: same ? c.seq : c.seq + 1 };
}

/** Close panel `index` and every panel right of it. */
export function close(c: Cascade, index: number): Cascade {
  if (index < 0 || index >= c.panels.length) return c;
  const panels = c.panels.slice(0, index);
  return { ...c, panels, expanded: keepExpanded(panels, c.expanded) };
}

/** Back one step: close the newest panel. */
export const back = (c: Cascade): Cascade => close(c, c.panels.length - 1);

/**
 * Reset: close every unpinned panel. Pinned ones stay in their order; a source stays when its
 * column is still there (the board, if `keepBoardSource` says the card is still on it; or a pinned
 * panel, re-indexed), else the panel shows as opened from its pin.
 */
export function reset(c: Cascade, isPinned: (p: Panel) => boolean, keepBoardSource: (s: PanelSource) => boolean = () => true): Cascade {
  const kept = c.panels.filter((p) => isPinned(p));
  const newColumn = new Map<number, number>([[0, 0]]);
  kept.forEach((p, i) => newColumn.set(columnOf(c.panels.indexOf(p)), columnOf(i)));
  const panels = kept.map((p): Panel => {
    const s = p.source;
    if (!s) return p;
    if (s.column === 0) return keepBoardSource(s) ? p : { ...p, source: null };
    const col = newColumn.get(s.column);
    return col != null ? { ...p, source: { ...s, column: col } } : { ...p, source: null };
  });
  return { ...c, panels, expanded: keepExpanded(panels, c.expanded) };
}

/**
 * "Open as board" on panel `index`: that project becomes the board, so the panel itself closes,
 * and with it every unpinned panel (their sources were on the board that is going away).
 */
export const openAsBoard = (c: Cascade, index: number, isPinned: IsPinned): Cascade => {
  const gone = c.panels[index]?.id;
  return reset(c, (p) => p.id !== gone && isPinned(p), () => false);
};

/** Expand a spine by hand (null: let the layout decide again). */
export const expand = (c: Cascade, id: number | null): Cascade => ({ ...c, expanded: id != null && c.panels.some((p) => p.id === id) ? id : null });

/**
 * A chain from a list of targets (a `?panels=` link, or Back/Forward): panel i came from column i
 * (the board for the first). `chain: false` (pins at sign-in): they came from nowhere. Panels of
 * `prev` that sit at the same place with the same target are kept, state and source included.
 */
export function fromTargets(targets: PanelTarget[], prev: Cascade = EMPTY, chain = true): Cascade {
  let seq = prev.seq;
  let same = true;
  const panels = targets.map((t, i): Panel => {
    const old = prev.panels[i];
    if (same && old && sameTarget(old, t)) return old;
    same = false;
    return { id: seq++, kind: t.kind, num: t.num, source: chain ? { column: i, num: t.num } : null };
  });
  return { panels, expanded: keepExpanded(panels, prev.expanded), seq };
}

// -- The URL ----------------------------------------------------------------------------

/** At most this many panels from a link (a chain longer than this is not one anybody reads). */
export const MAX_PANELS = 12;

/** "project:180,item:48,notes:48" */
export const serialise = (panels: PanelTarget[]): string => panels.map((p) => `${p.kind}:${p.num}`).join(",");

/** "project:180,item:48,notes:48" -> targets; anything malformed is skipped. */
export function parse(s: string | null | undefined): PanelTarget[] {
  if (!s) return [];
  const out: PanelTarget[] = [];
  for (const part of s.split(",")) {
    const m = part.trim().match(/^(project|item|notes):#?(\d{1,7})$/);
    if (m && Number(m[2]) > 0) out.push({ kind: m[1] as PanelKind, num: Number(m[2]) });
    if (out.length >= MAX_PANELS) break;
  }
  return out;
}

/** The panels named in a location's query string. */
export const readPanels = (search: string): PanelTarget[] => parse(new URLSearchParams(search).get("panels"));

/** The query string with `panels` set to this chain (or removed when there is none); "" or "?...". */
export function writePanels(search: string, panels: PanelTarget[]): string {
  const q = new URLSearchParams(search);
  if (panels.length) q.set("panels", serialise(panels));
  else q.delete("panels");
  // keep the separators readable: "project:180,item:48", not "project%3A180%2Citem%3A48"
  const s = q.toString().replace(/%3A/gi, ":").replace(/%2C/gi, ",");
  return s ? `?${s}` : "";
}

// -- Layout -----------------------------------------------------------------------------

/** The docked row (px): the board shrinks to BOARD_MIN before a panel folds into a SPINE. */
export const GAP = 20;
export const BOARD_MIN = 360;
export const BOARD_MAX = 1080;
export const PANEL_MIN = 380;
export const PANEL_MAX = 560;
export const SPINE = 36;
/** The sidebar shrinks to a rail of project codes with two or more panels between these widths. */
export const RAIL_FROM = 900;
export const RAIL_UNTIL = 1500;

export const railed = (viewport: number, panels: number) => panels >= 2 && viewport >= RAIL_FROM && viewport < RAIL_UNTIL;

/**
 * Which panels fold into spines so the row fits in `avail` px: the oldest first, never the one
 * expanded by hand, else never the newest. If even that does not fit, the row scrolls sideways.
 * (1366px four deep with the rail: board 398 + spine + spine + 380 + 380.)
 */
export function fold(avail: number, ids: number[], expanded: number | null = null): Set<number> {
  const folded = new Set<number>();
  const n = ids.length;
  if (!n) return folded;
  const keep = expanded != null && ids.includes(expanded) ? expanded : ids[n - 1];
  let need = BOARD_MIN + n * (GAP + PANEL_MIN);
  for (const id of ids) {
    if (need <= avail) break;
    if (id === keep) continue;
    folded.add(id);
    need -= PANEL_MIN - SPINE;
  }
  return folded;
}
